import React, {useEffect, useMemo, useState}					from 'react';
import {useNavigate}								from 'react-router-dom';
import moment										from 'moment';

import AddCard										from '../ressources/components/AddCard';
import Avatar										from '../ressources/components/Avatar';
import Select										from '../ressources/components/Select';
import UsersModalAssignDevice						from './UserAssignDevice';
import Badge										from '../ressources/components/Badge';
import Checkbox										from '../ressources/components/Checkbox';
import Button										from '../ressources/components/Button';
import Modal										from '../ressources/components/Modal';
import Input										from '../ressources/components/Input';

import {useAppDispatch, useAppSelector}				from '../app/hooks';
import {postCompanyUsersApi}						from '../redux/companyUsers/companyUsers';
import {User, UserNotNull}							from '../redux/user/interface';
import {LanguageType}								from '../ressources/language/type';
import {InputValues}								from '../ressources/components/Input/interface';
import {PostUserData}								from '../redux/companyUsers/interface';

import {ft_formatUserName}							from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';

import img_userPlus									from '../ressources/images/icon/user-plus-01.svg';
import img_users									from '../ressources/images/icon/users.svg';
import img_link										from '../ressources/images/icon/link.svg';

import LANGUAGE										from '../ressources/language';
import * as API										from '../ressources/API';

import './Onboarding.css';


interface ImportData {
	user: User,
	hris: keyof LanguageType['component']['externalApi'],
	selected: boolean
}

interface HideData {
	email: string,
	firstname: string,
	lastname: string,
	hris: keyof LanguageType['component']['externalApi'],
}

export default function Onboarding() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const users = useAppSelector(state => state.companyUsers.data);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const orders = useAppSelector(state => state.order.data);
	const lang = useAppSelector(state => state.settings.lang);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [hris, set_hris] = useState<(keyof LanguageType['component']['externalApi'])[]>([]);
	const [hrisUsers, set_hrisUsers] = useState<ImportData[]>([]);
	const [modal, set_modal] = useState<false | UserNotNull>(false);
	const [modalAdd, set_modalAdd] = useState<false | true | ImportData>(false);
	const [contractStart, set_contractStart] = useState<InputValues>('');

	const [loadPost, set_loadPost] = useState(false);

	const [hide, set_hide] = useState<HideData[]>([]);

	const navigate = useNavigate();
	const TEXTE = LANGUAGE(lang);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	useEffect(getData, []);

	useEffect(() => {
		if (hris.length)
			getHrisUsers();
	}, [hris]);

	function getData() {
		API.getCompanyHideOauth(user)
		.then((response) => {
			if (response.data)
				set_hide(response.data);
		});

		API.getUserOauth(user)
		.then((response) => {
			if (response.data)
				set_hris(response.data);
		});
	}

	function getHrisUsers(i = 0, data: ImportData[] = []) {
		if (!hris[i])
			return;

		API.importUserHris(hris[i], user)
		.then((response) => {
			if (!response?.data)
				return getHrisUsers(i + 1, data);

			const validImports: ImportData[] = [...data];

			response?.data?.forEach((imported: User) => {
				if (!imported?.email)
					return;
				if (users.some(find => find?.email.toLowerCase() === imported.email.toLowerCase()))
					return;
				if (users.some(find => ft_formatUserName(find) === ft_formatUserName(imported)))
					return;

				validImports.push({
					selected: false,
					hris:     hris[i],
					user:     imported
				});
			});

			set_hrisUsers(validImports);
			getHrisUsers(i + 1, validImports);
		});
	}

	async function addUsers() {
		let successCount = 0;
		set_loadPost(true);

		const useData: ImportData[] = typeof modalAdd === 'boolean' ? hrisUsers : [modalAdd];

		await Promise.all(useData.map(importData =>
			new Promise((resolve) => {
				if (!importData?.user || !importData.selected)
					return resolve(true);

				const apiData: PostUserData = {
					noHubspot:     true,
					addMethod:     importData.hris,
					job:           importData.user.job,
					email:         importData.user.email,
					phone:         importData.user.phone,
					lastname:      importData.user.lastname,
					firstname:     importData.user.firstname,
					contractEnd:   importData.user.contractEnd ? moment(importData.user.contractEnd).toDate() : undefined,
					contractStart: contractStart ? moment(contractStart, 'DD/MM/YYYY').toDate() : undefined
				};

				dispatch(postCompanyUsersApi({data: apiData, user}))
				.then(async (response) => {
					if (!response.payload._id) {
						const notificationData = ft_genNotification(TEXTE, 'error', img_link, response.payload.error, 'error');
						notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
					} else
						successCount++;

					resolve(true);
				});
			})
		));

		const notificationData = ft_genNotification(TEXTE, 'addMultipleUsersSuccess', img_link, successCount.toString());
		notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

		set_loadPost(false);
		set_modalAdd(false);
		getData();
	}

	function hideUsers(forceUser?: ImportData) {
		const useData: ImportData[] = forceUser ? [forceUser] : hrisUsers;
		const newHide = [...hide];
		const copy = [...hrisUsers];

		useData.map((importData) => {
			if (!importData?.user || !importData.selected)
				return;

			newHide.push({
				hris:      importData.hris,
				email:     importData.user.email,
				lastname:  importData.user.lastname,
				firstname: importData.user.firstname
			});
		});

		copy.forEach(e => e.selected = false);

		set_hide(newHide);
		set_hrisUsers(copy);

		API.putCompany(user?.company || '', {hideOauth: newHide}, user);
	}

	function renderUsers() {
		const onClickAll = () => {
			const copy = [...hrisUsers];

			copy.forEach(e => e.selected = !allSelected);

			set_hrisUsers(copy);
		};
		const render: React.ReactElement[] = [];
		const allSelected = !hrisUsers.some(find => !find.selected);

		hrisUsers.forEach((userData, i) => {
			if (hide.some(find => find.hris === userData.hris && find.email === userData.user?.email && find.firstname === userData.user?.firstname && find.lastname === userData.user?.lastname))
				return;

			const onClick = () => {
				const copy = [...hrisUsers];

				copy[i].selected = !copy[i].selected;

				set_hrisUsers(copy);
			};

			render.push(
				<tr
					key={`hris-${i}`}
					onClick={onClick}
					style={{cursor: 'pointer'}}
					className={userData.selected ? 'tableRow selected' : ''}
				>
					<td>
						<Checkbox active={userData.selected} />
					</td>
					<td>
						<Badge text={TEXTE.component.externalApi[userData.hris]} />
					</td>
					<td>
						<div className={'flex'} style={{alignItems: 'center', gap: 8}}>
							<Avatar size={40} user={userData.user} />
							{ft_formatUserName(userData.user)}
						</div>
					</td>
					<td>{userData.user?.contractStart ? moment(userData.user.contractStart).format('DD/MM/YYYY') : ''}</td>
					<td>
						<span className={'deviceStatus unstart'}>{TEXTE.users.onboarding.unstart}</span>
					</td>
					<td align={'right'}>
						<Select
							style={{alignSelf: 'flex-end'}}
							placeholderClassname={'weight-600 gray-600'}
							placeholder={TEXTE.users.onboarding.addToValala}
							data={[
								{value: 'add', render: TEXTE.users.onboarding.addToValala},
								{value: 'stock', render: TEXTE.users.onboarding.hide}
							]}
							onChange={(val) => {
								if (val === 'add')
									return set_modalAdd(userData);
								hideUsers(userData);
							}}
						/>
					</td>
				</tr>
			);
		});

		users.forEach((userData, i) => {
			if (!userData?.contractStart || moment(userData?.contractStart).isBefore(moment()))
				return;

			let status: 'complete' | 'unstart' | 'needAssign' = 'unstart';

			if (!devicesWithLines.some(device => userData && device.user === userData?._id) && !orders?.some(e => e?.addressList?.some(f => f?.devices?.some(g => g?.assign?.some(find => find === userData?._id)))))
				status = 'needAssign';
			else if (devicesWithLines)
				status = 'complete';

			render.push(
				<tr key={i}>
					<td />
					<td />
					<td>
						<div className={'flex'} style={{alignItems: 'center', gap: 8}}>
							<Avatar size={40} user={userData} />
							{ft_formatUserName(userData)}
						</div>
					</td>
					<td>{moment(userData.contractStart).format('DD/MM/YYYY')}</td>
					<td>
						<span className={`deviceStatus ${status}`}>{TEXTE.users.onboarding[status]}</span>
					</td>
					<td align={'right'}>
						{status !== 'unstart' &&
							<Select
								style={{alignSelf: 'flex-end'}}
								placeholder={TEXTE.users.onboarding.assign}
								placeholderClassname={'weight-600 gray-600'}
								data={[
									{value: 'order', render: TEXTE.order.newCommand},
									{value: 'stock', render: TEXTE.users.onboarding.fromStock}
								]}
								onChange={(val) => {
									if (val === 'order')
										return navigate(`/order/catalog?user=${userData._id}`);
									return set_modal(userData);
								}}
							/>
						}
					</td>
				</tr>
			);
		});

		return (
			<div className={'globalDataContainerItem column flex'} style={{overflow: 'auto'}}>

				<div className={'flex-row'} style={{gap: 8}}>
					<Button
						size={'sm'}
						type={'secondary-gray'}
						onClick={() => set_modalAdd(true)}
						disabled={!hrisUsers.some(find => find.selected)}
					>
						{TEXTE.users.onboarding.addToValala}
					</Button>
					<Button
						size={'sm'}
						type={'danger-reverse'}
						onClick={() => hideUsers()}
						disabled={!hrisUsers.some(find => find.selected)}
					>
						{TEXTE.users.onboarding.hide}
					</Button>
				</div>

				<table className={'importTable'}>
					<thead>
						<tr style={{textAlign: 'left'}}>
							<th style={{width: 50}}>
								{hrisUsers.length > 0 && <Checkbox onChange={onClickAll} active={allSelected} />}
							</th>
							<th style={{width: 60}}>{TEXTE.global.sirh}</th>
							<th>{TEXTE.global.name}</th>
							<th>{TEXTE.users.onboarding.startDate}</th>
							<th>{TEXTE.users.onboarding.status}</th>
							{/* <th>{TEXTE.global.devices}</th> */}
							<th align={'right'}>{TEXTE.global.action}</th>
						</tr>
					</thead>
					<tbody>
						{render}
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<div className={'globalDataContainer'}>
			<div className={'flex-row'} style={{gap: 16}}>
				<AddCard
					icon={img_userPlus}
					iconColor={'#ffffff'}
					backgroundColorIcon={'#678DE0'}
					title={TEXTE.users.addUserManually}
					text={TEXTE.users.addUserManuallyText}
					onClick={() => navigate('/users/onboarding/manually')}
				/>
				<AddCard
					icon={img_users}
					backgroundColorIcon={'#FFA465'}
					title={TEXTE.users.addUserImport}
					text={TEXTE.users.addUserImportText}
					onClick={() => navigate('/users/onboarding/import')}
				/>
				<AddCard
					icon={img_users}
					backgroundColorIcon={'#C36161'}
					title={TEXTE.users.addUserGoogle}
					text={TEXTE.users.addUserGoogleText}
					onClick={() => navigate('/users/hris/google')}
				/>
			</div>

			<br />
			{renderUsers()}
			<br />

			{modal &&
				<UsersModalAssignDevice
					modal
					actualUser={modal}
					onClose={() => set_modal(false)}
				/>
			}

			{modalAdd &&
				<Modal
					confirmLoad={loadPost}
					confirmButton={addUsers}
					onClose={() => set_modalAdd(false)}
					cancelButton={() => set_modalAdd(false)}
				>
					<span className={'text-lg weight-600 gray-900'}>{TEXTE.users.onboarding.addModal}</span>
					<br />
					<Input
						type={'date'}
						value={contractStart}
						placeholder={'JJ/MM/AAAA'}
						style={{marginBottom: 300}}
						onChange={set_contractStart}
					/>
				</Modal>
			}
		</div>
	);
}